import { debounce, throttle } from 'throttle-debounce'
import * as React from 'react'

import GoogleMapsClient from 'common/services/GoogleMapsClient'
import { captureGoogleMapsError } from 'embed/constants/errors'

import FormAutocomplete, { CommonProps as FormAutocompleteProps } from 'embed/components/FormAutocomplete'
import { shouldEnableGeoLocationSelector } from 'embed/selectors/config'
import { MultiProductState } from 'embed/reducers'
import { connect } from 'react-redux'

export interface Props extends FormAutocompleteProps {
  onAddressSelect?: (addressOne: string, city: string, state: string, zip: string) => void
  forceFocus?: boolean
  enableGeoLocation?: boolean
}

type AutocompleteItem = google.maps.places.AutocompletePrediction

const AUTOCOMPLETE_THROTTLE_INTERVAL = 800
const AUTOCOMPLETE_DEBOUNCE_INTERVAL = 1200

export const getPlacePredictions = (value: string, callback: (items: AutocompleteItem[]) => void) => {
  GoogleMapsClient.load
    .then((client) => {
      client.autocomplete.getPlacePredictions(
        { componentRestrictions: { country: 'us' }, input: value, types: ['address'] },
        (predictions: AutocompleteItem[], status: google.maps.places.PlacesServiceStatus) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            callback(predictions)
          }
        },
      )
    })
    .catch(captureGoogleMapsError)
}

export const getPlacePredictionsThrottled = throttle(AUTOCOMPLETE_THROTTLE_INTERVAL, getPlacePredictions)

export const getPlacePredictionsDebounced = debounce(AUTOCOMPLETE_DEBOUNCE_INTERVAL, getPlacePredictions)

// Based on UX pattern here
// https://www.peterbe.com/plog/how-to-throttle-and-debounce-an-autocomplete-input-in-react
export const getPlacePredictionsCurved = (value: string, callback: (items: AutocompleteItem[]) => void) => {
  if (value.endsWith(' ')) {
    getPlacePredictionsDebounced.cancel()
    return getPlacePredictionsThrottled(value, callback)
  } else if (value.length < 3) {
    return
  } else {
    getPlacePredictionsThrottled.cancel()
    return getPlacePredictionsDebounced(value, callback)
  }
}

export const AddressAutocompleteView = ({
  onAddressSelect,
  onChange,
  placeholder,
  enableGeoLocation,
  forceFocus,
  ...props
}: Props) => {
  const [items, setItems] = React.useState<AutocompleteItem[]>([])

  const handleGetItemValue = (prediction) => prediction.description as string

  const handleSelect = (value, prediction: google.maps.places.AutocompletePrediction) => {
    if (!enableGeoLocation) {
      onAddressSelect?.(prediction.structured_formatting.main_text, '', '', '')
      return
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const place_id = prediction.place_id
    GoogleMapsClient.load
      .then((client) => {
        const { places } = client
        const request = { placeId: place_id, fields: ['address_components'] }
        places.getDetails(
          request,
          (result: google.maps.places.PlaceResult, status: google.maps.places.PlacesServiceStatus) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
              return
            }
            const getComponentWithType = (components: google.maps.GeocoderAddressComponent[], type: string) =>
              components.find((component) => component.types.find((t) => t === type))
            const state =
              result.address_components &&
              getComponentWithType(result.address_components, 'administrative_area_level_1')
            const city =
              result.address_components &&
              (getComponentWithType(result.address_components, 'locality') ||
                getComponentWithType(result.address_components, 'sublocality'))
            const zip = result.address_components && getComponentWithType(result.address_components, 'postal_code')

            onAddressSelect?.(
              prediction.structured_formatting.main_text,
              city?.long_name ?? '',
              state?.short_name ?? '',
              zip?.long_name ?? '',
            )
          },
        )
      })
      .catch(captureGoogleMapsError)
    return prediction.structured_formatting.main_text
  }

  const handleChange = (event: React.FormEvent<HTMLInputElement>, value: string) => {
    // Call the onChange method passed in as a prop
    onChange?.(event, value)

    // Fetch the Google Places predictions based on the new address value
    if (value) {
      getPlacePredictionsCurved(value, setItems)
    }
  }

  const itemIdentity = (item: AutocompleteItem & { id: string }) => item.id ?? item.place_id

  return (
    <FormAutocomplete
      {...props}
      items={items}
      itemId={itemIdentity}
      getItemValue={handleGetItemValue}
      onChange={handleChange}
      onSelect={handleSelect}
      placeholder={placeholder ?? 'Enter your street address, apt, unit, etc.'}
      forceFocus={forceFocus}
    />
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  enableGeoLocation: shouldEnableGeoLocationSelector(state),
})

export const AddressAutocomplete = connect(mapStateToProps)(AddressAutocompleteView)
